import React from 'react';
import styled from 'styled-components';
import { EuiIcon } from '@elastic/eui';
import { BoxedResponsiveDashboardStyled } from '../ServerItem2';
import AlarmsWidget from '../serverWidgets/AlarmsWidget';
import BaseLineChart, { TooltipshiChartStyled } from '../../../../components/charts/BaseLineChart';
import { getConnectionsData, getMemoryData } from './CommonFuncs';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 4fr 2fr;
  grid-gap: 12px;
  margin: 0 10px 18px 10px;
  width: calc(100% - 20px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartContainer = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 12px;
`;

const ChartTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

const OpCounterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-gap: 8px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const OpChip = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${props => props.color || 'rgb(0, 128, 213)'};
    transform: translateY(-1px);
  }

  .label {
    font-size: 9px;
    color: rgba(255, 255, 255, 0.45);
    text-transform: uppercase;
    font-weight: 700;
  }

  .value {
    font-size: 14px;
    font-weight: 700;
    font-family: Consolas, monospace;
    color: ${props => props.color || '#fff'};
    margin-top: 4px;
  }
`;

const ReplicaCluster = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 11px;

  .node-host {
    font-family: Consolas, monospace;
    color: rgba(255, 255, 255, 0.8);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 170px;
  }
`;

const NodeStatusIcon = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.active ? '#50bc5e' : 'rgba(255, 255, 255, 0.2)'};
  box-shadow: ${props => props.active ? '0 0 6px #50bc5e' : 'none'};
  position: relative;
  
  ${props => props.pulse && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: inherit;
      border-radius: inherit;
      animation: pulse-glow 2s infinite ease-out;
    }
  `}

  @keyframes pulse-glow {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(3.5);
      opacity: 0;
    }
  }
`;

const NodeRoleBadge = styled.span`
  font-size: 8px;
  text-transform: uppercase;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  background: ${props => props.isPrimary ? 'rgba(80, 188, 94, 0.12)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.isPrimary ? '#50bc5e' : 'rgba(255, 255, 255, 0.55)'};
  border: 1px solid ${props => props.isPrimary ? 'rgba(80, 188, 94, 0.25)' : 'rgba(255, 255, 255, 0.08)'};
`;

const TagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  border-top: 1px dashed rgba(255, 255, 255, 0.05);
  padding-top: 10px;
`;

const TagBadge = styled.span`
  font-size: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  padding: 2px 6px;
  color: rgba(255, 255, 255, 0.8);

  span.label {
    color: rgba(255, 255, 255, 0.4);
    margin-right: 4px;
    font-weight: 500;
  }
`;

const MemoryDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  margin-top: 8px;
`;

const MemoryBadge = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  padding: 8px;
  text-align: center;
  font-size: 11px;

  .title {
    color: rgba(255, 255, 255, 0.45);
    margin-bottom: 4px;
    font-weight: 500;
  }
  .val {
    font-family: Consolas, monospace;
    font-weight: 700;
    color: ${props => props.color || '#fff'};
    font-size: 13px;
  }
`;

const CustomMemTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipshiChartStyled className="custom-tooltip">
                <div style={{ padding: '6px', fontSize: '11px' }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#fff' }}>{payload[0].payload.name}</p>
                    {payload.map((pld, index) => (
                        <p key={index} style={{ margin: '2px 0', color: pld.stroke || pld.color }}>
                            {pld.name === 'resident' ? 'Resident' : 'Virtual'}: {pld.value} MB
                        </p>
                    ))}
                </div>
            </TooltipshiChartStyled>
        );
    }
    return null;
};

const MongoMetricsPanel = ({ server, alarms, metrics }) => {
    if (!server || !server.metric) return null;

    const serverStatus = server.metric.serverStatus || {};
    const repl = serverStatus.repl || {};
    const replicaHosts = Array.isArray(repl.hosts) ? repl.hosts : [];
    const opcounterValues = serverStatus.opcounters || {};

    const connectionsChart = getConnectionsData(metrics);
    const memoryChart = getMemoryData(metrics);

    const opCounters = [
        { name: 'Insert', val: opcounterValues.insert, color: '#0080d5' },
        { name: 'Query', val: opcounterValues.query, color: '#50bc5e' },
        { name: 'Update', val: opcounterValues.update, color: '#f9be49' },
        { name: 'Delete', val: opcounterValues.delete, color: '#F86B63' },
        { name: 'Get More', val: opcounterValues.getmore, color: '#9f5afd' },
        { name: 'Command', val: opcounterValues.command, color: '#00c5c3' }
    ];

    return (
        <PanelGrid>
            {/* Left Column: Charts, Mem, and OpCounters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Charts */}
                <BoxedResponsiveDashboardStyled className="column-body">
                    <SectionHeader><EuiIcon type="visualizeApp" size="m"/> Analytics & Performance</SectionHeader>
                    <ChartsGrid>
                        <ChartContainer>
                            <ChartTitle>Connections History</ChartTitle>
                            {connectionsChart && connectionsChart.data.length > 0 ? (
                                <BaseLineChart data={connectionsChart} />
                            ) : (
                                <div style={{ height: 180, display: 'grid', placeItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                                    No historical data
                                </div>
                            )}
                        </ChartContainer>

                        <ChartContainer>
                            <ChartTitle>Memory Usage History (MB)</ChartTitle>
                            {memoryChart && memoryChart.data.length > 0 ? (
                                <ResponsiveContainer width="100%" height={230}>
                                    <AreaChart data={memoryChart.data}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgb(52,55,65)"/>
                                        <XAxis dataKey="name" stroke="rgba(255, 255, 255, 0.65)" fontSize={10}/>
                                        <YAxis stroke="rgba(255, 255, 255, 0.65)" fontSize={10} unit="M"/>
                                        <Tooltip content={<CustomMemTooltip />}/>
                                        <Area type="monotone" name="virtual" dataKey="virtual" stroke="#f9be49" fill="rgba(249, 190, 73, 0.05)" strokeWidth={1.5}/>
                                        <Area type="monotone" name="resident" dataKey="resident" stroke="#50bc5e" fill="rgba(80, 188, 94, 0.15)" strokeWidth={2}/>
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ height: 180, display: 'grid', placeItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                                    No historical data
                                </div>
                            )}
                            <MemoryDetailsGrid>
                                <MemoryBadge color="#50bc5e">
                                    <div className="title">Resident Memory</div>
                                    <div className="val">{server.memResident || 0} MB</div>
                                </MemoryBadge>
                                <MemoryBadge color="#f9be49">
                                    <div className="title">Virtual Memory</div>
                                    <div className="val">{server.memVirtual || 0} MB</div>
                                </MemoryBadge>
                            </MemoryDetailsGrid>
                        </ChartContainer>
                    </ChartsGrid>
                </BoxedResponsiveDashboardStyled>

                {/* Operations Counters */}
                <BoxedResponsiveDashboardStyled className="column-body">
                    <SectionHeader><EuiIcon type="compute" size="m"/> Operations Counters (CRUD)</SectionHeader>
                    <OpCounterGrid>
                        {opCounters.map((op, idx) => (
                            <OpChip key={idx} color={op.color}>
                                <span className="label">{op.name}</span>
                                <span className="value">{op.val}</span>
                            </OpChip>
                        ))}
                    </OpCounterGrid>
                </BoxedResponsiveDashboardStyled>
            </div>

            {/* Right Column: Replication Status and Alarms */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Replication Status */}
                <BoxedResponsiveDashboardStyled className="column-body">
                    <SectionHeader><EuiIcon type="mapMarker" size="m"/> Replication Cluster</SectionHeader>
                    <ReplicaCluster>
                        {/* Primary Node */}
                        {repl.primary && (
                            <NodeRow style={{ borderColor: 'rgba(80, 188, 94, 0.25)', background: 'rgba(80, 188, 94, 0.015)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <NodeStatusIcon active={true} pulse={true} />
                                    <span className="node-host" title={repl.primary}>{repl.primary}</span>
                                </div>
                                <NodeRoleBadge isPrimary={true}>Primary</NodeRoleBadge>
                            </NodeRow>
                        )}
                        {/* Secondary Nodes */}
                        {replicaHosts.filter(host => host !== repl.primary).map((host, idx) => (
                            <NodeRow key={idx}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <NodeStatusIcon active={true} pulse={false} />
                                    <span className="node-host" title={host}>{host}</span>
                                </div>
                                <NodeRoleBadge isPrimary={false}>Secondary</NodeRoleBadge>
                            </NodeRow>
                        ))}
                    </ReplicaCluster>

                    {/* Replication / Cluster Tags */}
                    {repl.tags && (
                        <TagsGrid>
                            {repl.tags.nodeType && (
                                <TagBadge><span className="label">Node:</span>{repl.tags.nodeType}</TagBadge>
                            )}
                            {repl.tags.provider && (
                                <TagBadge><span className="label">Cloud:</span>{repl.tags.provider}</TagBadge>
                            )}
                            {repl.tags.region && (
                                <TagBadge><span className="label">Region:</span>{repl.tags.region}</TagBadge>
                            )}
                            {repl.tags.workloadType && (
                                <TagBadge><span className="label">Workload:</span>{repl.tags.workloadType}</TagBadge>
                            )}
                        </TagsGrid>
                    )}
                </BoxedResponsiveDashboardStyled>

                {/* Alarms widget */}
                <BoxedResponsiveDashboardStyled className="column-body">
                    <AlarmsWidget alarms={alarms} server={server} />
                </BoxedResponsiveDashboardStyled>
            </div>
        </PanelGrid>
    );
};

export default MongoMetricsPanel;
